const initLazyImages = (root = document) => {
  const lazyImages = root.querySelectorAll('img[loading="lazy"]');
  lazyImages.forEach((img) => {
    img.setAttribute("loaded", img.complete ? "true" : "false");
    img.onload = () => {
      img.setAttribute("loaded", "true");
    };
  });
};

document.addEventListener("DOMContentLoaded", () => {
  initLazyImages();
  initCompanyBlockInteractions();
  bindCompanyBlockOutsideClose();
  initCompanyBlockMobileToggle();
  bindLegalModals();

  // Throttle and observer for autoplay efficiency
  let throttleTick = false;
  const handleVideoIntersection = (video) => {
    if (video.isIntersecting) {
      video.target.play();
    } else {
      video.target.pause();
    }
    throttleTick = false;
  };
  const observerCallback = (entries) => {
    if (throttleTick) return;
    throttleTick = true;
    window.requestAnimationFrame(() => {
      entries.forEach(handleVideoIntersection);
    });
  };
  const observer = new IntersectionObserver(observerCallback);

  const videos = document.querySelectorAll("video[data-src]");
  videos.forEach((video) => {
    video.src = video.dataset.src;
    video.poster = video.dataset.poster;
    video.setAttribute("loaded", "true");

    if (video.hasAttribute("data-autoplay")) {
      video.play();
      // If the video is still paused it's probably in lower mode and not going to play at all
      if (video.paused) video.removeAttribute("src");
      if (video.paused) {
        window.lowPowerModeDetected = true;
      }

      // Only play the video when it's in view
      observer.observe(video);
    } else {
      video.dispatchEvent(new Event("lazyHandled"));
    }
  });
});

document.addEventListener("shopify:section:load", (event) => {
  if (!event.target) return;
  initLazyImages(event.target);
  initCompanyBlockInteractions(event.target);
  bindCompanyBlockOutsideClose();
  initCompanyBlockMobileToggle(event.target);
  bindLegalModals(event.target);
});

// Prevent normal link interaction when multiple links exist and log wrapper clicks
const initCompanyBlockInteractions = (root = document) => {
  const wrappers = root.querySelectorAll(
    ".company-block__links-wrapper.has-multiple"
  );
  wrappers.forEach((wrapper) => {
    if (wrapper.dataset.cbBound === "true") return;

    // Click handler on the wrapper
    wrapper.addEventListener("click", (e) => {
      // If already raised and the click is on a link, allow the link to act and do not toggle
      if (
        wrapper.classList.contains("raised") &&
        e.target.closest(".company-block__link")
      ) {
        return;
      }
      // Toggle the raised class to animate links upward by --card-height
      wrapper.classList.toggle("raised");
    });

    wrapper.dataset.cbBound = "true";
  });
};

// Mobile: toggle visibility of links wrapper via the "N Links" trigger
const initCompanyBlockMobileToggle = (root = document) => {
  const triggers = root.querySelectorAll(
    ".links-mobile-trigger[aria-controls]"
  );
  triggers.forEach((trigger) => {
    if (trigger.dataset.cbMobileBound === "true") return;
    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const targetId = trigger.getAttribute("aria-controls");
      if (!targetId) return;
      const wrapper = document.getElementById(targetId);
      if (!wrapper) return;

      const expanded = trigger.getAttribute("aria-expanded") === "true";
      const nextExpanded = !expanded;
      trigger.setAttribute("aria-expanded", String(nextExpanded));
      // Ensure desktop-specific state doesn't interfere on mobile
      wrapper.classList.remove("raised");

      if (nextExpanded) {
        openMobileLinks(wrapper);
      } else {
        closeMobileLinks(wrapper);
      }
    });
    trigger.dataset.cbMobileBound = "true";
  });
};

// Helpers to animate mobile open/close via max-height and opacity (avoid height:auto bounce)
const openMobileLinks = (wrapper) => {
  // Mark visible first so scrollHeight reflects full content
  wrapper.setAttribute("aria-hidden", "false");
  // Animate max-height from 0 to content height
  const target = wrapper.scrollHeight;
  // Set the destination; CSS handles transition of max-height
  wrapper.style.maxHeight = target + "px";

  // If images load and content grows, bump max-height accordingly
  const imgs = wrapper.querySelectorAll("img");
  imgs.forEach((img) => {
    if (img.complete) return;
    img.addEventListener(
      "load",
      () => {
        const newH = wrapper.scrollHeight;
        if (newH > parseInt(wrapper.style.maxHeight || 0, 10)) {
          wrapper.style.maxHeight = newH + "px";
        }
      },
      { once: true }
    );
  });
};

const closeMobileLinks = (wrapper) => {
  // From current content height -> 0 using max-height
  const current = wrapper.scrollHeight;
  wrapper.style.maxHeight = current + "px";
  // Force reflow to ensure transition starts from current value
  // eslint-disable-next-line no-unused-expressions
  wrapper.offsetHeight;
  wrapper.style.maxHeight = "0px";
  wrapper.setAttribute("aria-hidden", "true");
};

// Close (remove 'raised') when clicking outside any raised wrapper; bind once
let cbOutsideCloseBound = false;
const bindCompanyBlockOutsideClose = () => {
  if (cbOutsideCloseBound) return;
  document.addEventListener("click", (e) => {
    // Desktop: collapse raised stacks when clicking outside
    const raised = document.querySelectorAll(
      ".company-block__links-wrapper.has-multiple.raised"
    );
    let clickInsideRaised = false;
    for (const wrapper of raised) {
      if (wrapper.contains(e.target)) {
        clickInsideRaised = true;
        break;
      }
    }
    if (!clickInsideRaised && raised.length) {
      raised.forEach((w) => w.classList.remove("raised"));
    }

    // Mobile: close any open wrappers (aria-hidden=false) when clicking outside
    const mobileOpen = document.querySelectorAll(
      '.company-block__links-wrapper.has-multiple[aria-hidden="false"]'
    );
    mobileOpen.forEach((w) => {
      const trigger = document.querySelector(
        `.links-mobile-trigger[aria-controls="${w.id}"]`
      );
      if (w.contains(e.target) || (trigger && trigger.contains(e.target)))
        return;
      closeMobileLinks(w);
      if (trigger) trigger.setAttribute("aria-expanded", "false");
    });
  });
  cbOutsideCloseBound = true;
};

// Intercept footer legal links and open corresponding modals
const bindLegalModals = (root = document) => {
  const legalLinks = root.querySelectorAll(
    ".footer__legal-menu .footer__legal_menu_link"
  );
  if (!legalLinks.length) return;

  const matchers = [
    {
      label: "PRIVACY POLICY",
      open: () => window.HulcanPrivacyModal?.open?.(),
    },
    {
      label: "TERMS & CONDITIONS",
      open: () => window.HulcanTermsModal?.open?.(),
    },
    { label: "COOKIE POLICY", open: () => window.HulcanCookieModal?.open?.() },
  ];

  legalLinks.forEach((a) => {
    if (a.dataset.modalBound === "true") return;
    const text = a.textContent.trim().toUpperCase();
    const match = matchers.find((m) => m.label === text);
    if (!match) return;
    a.addEventListener("click", (e) => {
      const opener = match.open;
      if (typeof opener === "function") {
        e.preventDefault();
        e.stopPropagation();
        opener();
      }
    });
    a.dataset.modalBound = "true";
  });
};
